/**
 * BONIFAKTUR theme - main JS entry point
 * ES2020, no build system, no frameworks
 */

(function () {
  'use strict';

  // ─── Utilities ──────────────────────────────────────────

  const debounce = (fn, delay) => {
    let timer;
    return (...args) => {
      clearTimeout(timer);
      timer = setTimeout(() => fn(...args), delay);
    };
  };

  function trapFocus(container) {
    const focusable = container.querySelectorAll(
      'a[href],button:not([disabled]),input,select,textarea,[tabindex]:not([tabindex="-1"])'
    );
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    container.addEventListener('keydown', (e) => {
      if (e.key !== 'Tab') return;
      if (e.shiftKey) {
        if (document.activeElement === first) { e.preventDefault(); last.focus(); }
      } else {
        if (document.activeElement === last) { e.preventDefault(); first.focus(); }
      }
    });
  }

  function openOverlay(overlay, trigger) {
    overlay.setAttribute('aria-hidden', 'false');
    overlay.removeAttribute('inert');
    if (trigger) trigger.setAttribute('aria-expanded', 'true');
  }

  function closeOverlay(overlay, trigger) {
    overlay.setAttribute('aria-hidden', 'true');
    overlay.setAttribute('inert', '');
    if (trigger) trigger.setAttribute('aria-expanded', 'false');
  }

  // ─── Announcement bar dismiss ───────────────────────────

  const announcementBar = document.querySelector('[data-announcement-bar]');
  if (announcementBar) {
    if (sessionStorage.getItem('announcement-dismissed')) {
      announcementBar.setAttribute('hidden', '');
    } else {
      const dismissBtn = announcementBar.querySelector('[data-dismiss]');
      if (dismissBtn) {
        dismissBtn.addEventListener('click', () => {
          announcementBar.setAttribute('hidden', '');
          sessionStorage.setItem('announcement-dismissed', '1');
        });
      }
    }
  }

  // ─── Desktop dropdown nav ───────────────────────────────
  // CSS handles hover; JS adds keyboard (Enter/Space/Escape) and click support.

  document.querySelectorAll('[data-nav-item]').forEach(item => {
    const toggle = item.querySelector('[data-nav-toggle-item]');
    const drop   = item.querySelector('[data-nav-drop]');
    if (!toggle || !drop) return;

    function openDrop() {
      item.setAttribute('data-open', '');
      toggle.setAttribute('aria-expanded', 'true');
      drop.setAttribute('aria-hidden', 'false');
    }
    function closeDrop() {
      item.removeAttribute('data-open');
      toggle.setAttribute('aria-expanded', 'false');
      drop.setAttribute('aria-hidden', 'true');
    }

    toggle.addEventListener('click', () => {
      item.hasAttribute('data-open') ? closeDrop() : openDrop();
    });

    item.addEventListener('keydown', e => {
      if (e.key === 'Escape') { closeDrop(); toggle.focus(); }
    });

    // Close when focus leaves the item entirely
    item.addEventListener('focusout', e => {
      if (!item.contains(e.relatedTarget)) closeDrop();
    });
  });

  // Close all drops on outside click
  document.addEventListener('click', e => {
    if (!e.target.closest('[data-nav-item]')) {
      document.querySelectorAll('[data-nav-item][data-open]').forEach(item => {
        item.removeAttribute('data-open');
        const t = item.querySelector('[data-nav-toggle-item]');
        if (t) t.setAttribute('aria-expanded', 'false');
        const d = item.querySelector('[data-nav-drop]');
        if (d) d.setAttribute('aria-hidden', 'true');
      });
    }
  });

  // ─── Mobile nav ─────────────────────────────────────────

  const navMenu    = document.querySelector('[data-nav-menu]');
  const navToggle  = document.querySelector('[data-nav-toggle]');
  const navClose   = document.querySelector('[data-nav-close]');

  function openNav() {
    if (!navMenu) return;
    openOverlay(navMenu, navToggle);
    document.body.classList.add('nav-open');
    trapFocus(navMenu);
    const firstFocusable = navMenu.querySelector('a, button');
    if (firstFocusable) firstFocusable.focus();
  }

  function closeNav() {
    if (!navMenu) return;
    closeOverlay(navMenu, navToggle);
    document.body.classList.remove('nav-open');
    if (navToggle) navToggle.focus();
  }

  if (navToggle) navToggle.addEventListener('click', openNav);
  if (navClose)  navClose.addEventListener('click', closeNav);

  // Defer-render heavy alpha brand lists. They ship inside <template>
  // tags so they don't count toward the initial DOM tree; clone the
  // contents into the parent <ul> on first <details> open.
  if (navMenu) {
    navMenu.addEventListener('toggle', function (e) {
      const d = e.target;
      if (!(d instanceof HTMLDetailsElement) || !d.open) return;
      const tpl = d.querySelector('template[data-menu-defer]');
      if (!tpl) return;
      tpl.parentElement.appendChild(tpl.content.cloneNode(true));
      tpl.remove();
    }, true); // capture: <details> toggle doesn't bubble
  }

  // ─── Search overlay ─────────────────────────────────────

  const searchOverlay = document.querySelector('[data-search-overlay]');
  const searchToggles = document.querySelectorAll('[data-search-toggle]'); // mobile + desktop
  const searchClose   = document.querySelector('[data-search-close]');
  const searchInput   = document.querySelector('[data-search-input]');
  let activeSearchToggle = null; // the button that opened the overlay (for focus return)

  function openSearch(e) {
    if (!searchOverlay) return;
    activeSearchToggle = (e && e.currentTarget) || searchToggles[0];
    openOverlay(searchOverlay, activeSearchToggle);
    trapFocus(searchOverlay);
    if (searchInput) searchInput.focus();
  }

  function closeSearch() {
    if (!searchOverlay) return;
    closeOverlay(searchOverlay, activeSearchToggle);
    if (activeSearchToggle) activeSearchToggle.focus();
  }

  searchToggles.forEach((toggle) => toggle.addEventListener('click', openSearch));
  if (searchClose) searchClose.addEventListener('click', closeSearch);

  // ─── Escape key closes any open overlay ─────────────────

  document.addEventListener('keydown', (e) => {
    if (e.key !== 'Escape') return;
    if (navMenu && navMenu.getAttribute('aria-hidden') === 'false') closeNav();
    if (searchOverlay && searchOverlay.getAttribute('aria-hidden') === 'false') closeSearch();
  });

  // ─── Click outside mobile nav to close ──────────────────

  document.addEventListener('click', (e) => {
    if (!navMenu || navMenu.getAttribute('aria-hidden') !== 'false') return;
    if (!navMenu.contains(e.target) && !navToggle.contains(e.target)) closeNav();
  });

  // ─── Sticky header shadow ────────────────────────────────

  const siteHeader = document.querySelector('[data-site-header]');
  if (siteHeader) {
    const updateHeaderState = debounce(() => {
      siteHeader.classList.toggle('is-scrolled', window.scrollY > 10);
    }, 10);
    window.addEventListener('scroll', updateHeaderState, { passive: true });
    updateHeaderState();
  }

})();
